import React, { useCallback, useEffect, useState } from "react";
import { CheckCircle2, ChevronDown, Circle, ListChecks, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const HIDE_KEY = "demo_checklist_hidden";

// NACSA presenter checklist — demo workspaces only. Steps tick automatically from real activity.
export const DemoChecklist = ({ hidden, setHidden }) => {
  const [steps, setSteps] = useState(null);
  const [open, setOpen] = useState(() => window.innerWidth >= 768);
  const load = useCallback(() => {
    fetch(`${API}/api/context/demo/progress`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null)).then((d) => d && setSteps(d.steps)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    window.addEventListener("focus", load);
    return () => { clearInterval(t); window.removeEventListener("focus", load); };
  }, [load]);
  if (hidden || !steps) return null;
  const done = steps.filter((s) => s.done).length;
  const next = steps.find((s) => !s.done);
  return (
    <div data-testid="demo-checklist" role="region" aria-label="NACSA demo steps"
      className="fixed bottom-[44px] right-3 z-[64] w-[min(300px,calc(100vw-24px))] rounded-xl border border-amber-300 bg-white shadow-lg">
      <div className="flex items-center gap-2 px-3 py-2">
        <ListChecks className="h-4 w-4 text-amber-600" />
        <button onClick={() => setOpen(!open)} aria-expanded={open} data-testid="demo-checklist-toggle"
          className="flex min-h-[32px] flex-1 items-center justify-between text-left text-[13px] font-semibold text-slate-900">
          <span>NACSA demo · {done}/{steps.length}</span>
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        <button onClick={() => { localStorage.setItem(HIDE_KEY, "1"); setHidden(true); }} aria-label="Hide checklist"
          data-testid="demo-checklist-hide" className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
          <X className="h-4 w-4" />
        </button>
      </div>
      {open && (
        <ol className="space-y-1 border-t border-amber-100 px-3 py-2">
          {steps.map((s, i) => (
            <li key={s.key} data-testid={`demo-step-${s.key}`} data-done={s.done}
              className={`flex items-start gap-2 text-[12.5px] ${s.done ? "text-slate-400 line-through" : next?.key === s.key ? "font-semibold text-slate-900" : "text-slate-600"}`}>
              {s.done ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> : <Circle className="mt-0.5 h-4 w-4 shrink-0 text-slate-300" />}
              <span>{i + 1}. {s.label}</span>
            </li>
          ))}
          {!next && <li className="pt-1 text-[12px] text-emerald-700" data-testid="demo-checklist-complete">All steps done. Use Reset My Demo for a fresh start.</li>}
        </ol>
      )}
    </div>
  );
};

export const isChecklistHidden = () => localStorage.getItem(HIDE_KEY) === "1";
export const showChecklist = () => localStorage.removeItem(HIDE_KEY);
